import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Admin.css';

const CreatePaymentLink = () => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    amount: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    expiryDate: '',
    maxPayments: '1'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(null);

    try {
      const response = await axios.post(
        'http://localhost:5000/api/admin/create-payment-link',
        formData
      );

      if (response.data.success) {
        setSuccess({
          message: 'Payment link created successfully!',
          linkId: response.data.linkId,
          paymentUrl: response.data.paymentUrl
        });
        
        // Reset form
        setFormData({
          title: '',
          description: '',
          amount: '',
          customerName: '',
          customerEmail: '',
          customerPhone: '',
          expiryDate: '',
          maxPayments: '1'
        });

        // Auto-scroll to success message
        setTimeout(() => {
          const successElement = document.getElementById('payment-success-message');
          if (successElement) {
            successElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create payment link');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // Show a temporary tooltip instead of alert
    const tooltip = document.createElement('div');
    tooltip.className = 'copy-tooltip';
    tooltip.textContent = 'Copied to clipboard!';
    document.body.appendChild(tooltip);
    setTimeout(() => {
      tooltip.classList.add('show');
      setTimeout(() => {
        tooltip.classList.remove('show');
        setTimeout(() => {
          document.body.removeChild(tooltip);
        }, 300);
      }, 1500);
    }, 10);
  };

  return (
    <div className="admin-create-link">
      <div className="create-link-header">
        <div className="header-left">
          <h1>
            <span className="gradient-text">Create Payment Link</span>
          </h1>
          <p className="header-subtitle">Generate a new payment link for your customers</p>
        </div>
        <button 
          className="back-btn"
          onClick={() => navigate('/admin/dashboard')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to Dashboard
        </button>
      </div>

      {error && (
        <div className="error-message">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="create-link-form animate-fadeIn">
        <div className="form-section">
          <h2 className="section-title">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            Basic Information
          </h2>
          <div className="form-card">
            <div className="form-group">
              <label>
                Title <span className="required">*</span>
                <span className="tooltip-icon" title="Enter a descriptive title for this payment">ⓘ</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="e.g., South India Tour Package"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>
                Description
                <span className="tooltip-icon" title="Add details about what this payment is for">ⓘ</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="4"
                placeholder="Package details, inclusions, terms, etc."
                className="form-textarea"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2 className="section-title">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23"/>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
            Payment Details
          </h2>
          <div className="form-card">
            <div className="form-row">
              <div className="form-group">
                <label>
                  Amount (₹) <span className="required">*</span>
                </label>
                <div className="amount-input-wrapper">
                  <span className="currency-symbol">₹</span>
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    required
                    min="1"
                    step="0.01"
                    placeholder="1000"
                    className="form-input amount-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>
                  Max Payments
                  <span className="tooltip-icon" title="Number of times this link can be used (default: 1)">ⓘ</span>
                </label>
                <input
                  type="number"
                  name="maxPayments"
                  value={formData.maxPayments}
                  onChange={handleChange}
                  min="1"
                  placeholder="1"
                  className="form-input"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2 className="section-title">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            Customer Information
            <span className="optional-badge">Optional</span>
          </h2>
          <div className="form-card">
            <div className="form-row">
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleChange}
                  placeholder="John Doe"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  name="customerEmail"
                  value={formData.customerEmail}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  name="customerPhone"
                  value={formData.customerPhone}
                  onChange={handleChange}
                  placeholder="9876543210"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Expiry Date</label>
                <input
                  type="datetime-local"
                  name="expiryDate"
                  value={formData.expiryDate}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            className="cancel-btn"
            onClick={() => navigate('/admin/dashboard')}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="submit-btn"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Creating...
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12h14"/>
                </svg>
                Create Payment Link
              </>
            )}
          </button>
        </div>
      </form>

      {/* Success Message - Moved outside form and positioned at bottom */}
      {success && (
        <div id="payment-success-message" className="success-message-bottom animate-slideUp">
          <div className="success-header">
            <div className="success-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <div className="success-title">
              <h3>{success.message}</h3>
              <p>Your payment link has been generated and is ready to share</p>
            </div>
            <button className="close-success" onClick={() => setSuccess(null)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          
          <div className="success-details">
            <div className="detail-item">
              <span className="detail-label">Link ID:</span>
              <span className="detail-value">{success.linkId}</span>
            </div>
            
            <div className="detail-item">
              <span className="detail-label">Payment URL:</span>
              <div className="url-container-large">
                <input 
                  type="text" 
                  value={success.paymentUrl} 
                  readOnly 
                />
                <button 
                  className="copy-btn-large"
                  onClick={() => copyToClipboard(success.paymentUrl)}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                  Copy Link
                </button>
              </div>
            </div>
          </div>

          <div className="success-actions">
            <button 
              className="secondary-action"
              onClick={() => navigate('/admin/payment-links')}
            >
              View All Links
            </button>
            <button 
              className="primary-action"
              onClick={() => {
                setSuccess(null);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              Create Another Link
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatePaymentLink;